import { getPidStatusFilterForQuery, type PidStatusFilter } from '../ManageTickets/pidStatusFilter';
import { DELISTED_TAB_ORDER_BY, MANAGE_TICKETS_ORDER_BY } from '../ManageTickets/manageTicketSort';
import type { ManageTicketTableSort } from '../ManageTickets/manageTicketTableSort';
import { getFailedTransferCutoffIso } from '../ManageTickets/utils/failToTransfer';
import {
  parseFilterCalendarDate,
  toLocalDateYmd,
} from './ticketEventDateFilter';

export type UserTicketSoldSubFilter =
  | 'all'
  | 'sold'
  | 'settled'
  | 'settlementinprogress'
  | 'failed'
  | 'voidedpayout'
  | 'paidmanuallyviaqa'
  | 'qadeniedpayout'
  | 'underreview';

export type UserTicketListQueryInput = {
  userId: string;
  tab: string;
  soldSubFilter: UserTicketSoldSubFilter;
  search: string;
  dateFilter: 'today' | 'yesterday' | 'thisWeek' | 'thisMonth' | 'custom' | 'all';
  customFromDate: string;
  customToDate: string;
  barcodeFilter: 'all' | 'attached' | 'missing' | 'none';
  pidStatusFilter: PidStatusFilter;
  vlShareFilter: 'all' | 'shared' | 'unshared';
  pricingMethodFilter: 'all' | 'MANUAL' | 'FOLLOW_MARKET' | 'NONE';
  onHoldOnly: boolean;
  lastPricedRange: { from: string; to: string } | null;
  lastPricedCustomRange: { from: string; to: string } | null;
  lastPricedBuckets: string[];
  stripeMatches: boolean;
  sort: ManageTicketTableSort | null;
  statusFilter?: string;
  now?: Date;
};

const SOLD_STATUSES = ['Sold', 'PaidManuallyViaQA', 'QADeniedPayout', 'UnderReview'];

const TAB_STATUSES: Record<string, string[] | null> = {
  All: null,
  Verification: ['ToBeVerified', 'Verified', 'Invalid'],
  Listed: ['List', 'Listed'],
  Sold: SOLD_STATUSES,
  Delisted: ['Delist', 'DelistInProgress', 'DelistRequested', 'Return'],
  Deleted: ['Deleted'],
  'Listing Request Cancelled': ['ListingRequestCancelled'],
  Unsold: ['Unsold'],
  Expired: ['Closed', 'ListingRequestExpired'],
};

const notVoidedPayout = {
  _or: [
    { tp_payout_status: { _is_null: true } },
    { tp_payout_status: { _neq: 'Voided_Payout' } },
  ],
};

const attachedBarcode = { tp_barcode_status: { _eq: 'Barcode Available' } };

const missingBarcode = {
  _and: [
    { tp_barcode_status: { _eq: 'Barcode Pending' } },
    { _not: { tp_barcode_attached: { _eq: true } } },
    { _or: [{ tp_barcode: { _is_null: true } }, { tp_barcode: { _eq: '' } }] },
  ],
};

const noBarcode = { tp_barcode_status: { _eq: 'Null' } };

const emptyUserTicketWhere = (userId: string) => ({
  _and: [{ u_id: { _eq: userId } }, { tp_id: { _is_null: true } }],
});

const dateBounds = (
  filter: UserTicketListQueryInput['dateFilter'],
  customFromDate: string,
  customToDate: string,
  now: Date,
): { fromYmd: string; toYmd: string } | null => {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (filter === 'all') return null;
  if (filter === 'today') {
    const ymd = toLocalDateYmd(today);
    return { fromYmd: ymd, toYmd: ymd };
  }
  if (filter === 'yesterday') {
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const ymd = toLocalDateYmd(yesterday);
    return { fromYmd: ymd, toYmd: ymd };
  }
  if (filter === 'thisWeek') {
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - today.getDay());
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    return { fromYmd: toLocalDateYmd(weekStart), toYmd: toLocalDateYmd(weekEnd) };
  }
  if (filter === 'thisMonth') {
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return { fromYmd: toLocalDateYmd(monthStart), toYmd: toLocalDateYmd(monthEnd) };
  }
  const from = parseFilterCalendarDate(customFromDate);
  const to = parseFilterCalendarDate(customToDate);
  if (!from || !to || from > to) return null;
  return { fromYmd: toLocalDateYmd(from), toYmd: toLocalDateYmd(to) };
};

const soldSubFilterClause = (soldSubFilter: UserTicketSoldSubFilter) => {
  if (soldSubFilter === 'paidmanuallyviaqa') return { tp_status: { _eq: 'PaidManuallyViaQA' } };
  if (soldSubFilter === 'qadeniedpayout') return { tp_status: { _eq: 'QADeniedPayout' } };
  if (soldSubFilter === 'underreview') return { tp_status: { _eq: 'UnderReview' } };
  if (soldSubFilter === 'voidedpayout') {
    return {
      _and: [
        { tp_status: { _eq: 'Sold' } },
        { tp_payout_status: { _eq: 'Voided_Payout' } },
      ],
    };
  }
  if (soldSubFilter === 'settled' || soldSubFilter === 'settlementinprogress' || soldSubFilter === 'failed') {
    const paymentStatus = soldSubFilter === 'settled'
      ? 'Success'
      : soldSubFilter === 'settlementinprogress'
        ? 'Inprogress'
        : 'Failed';
    return {
      _and: [
        { tp_status: { _eq: 'Sold' } },
        { tp_payment_status: { _eq: paymentStatus } },
        notVoidedPayout,
      ],
    };
  }
  if (soldSubFilter === 'sold') {
    return {
      _and: [
        { tp_status: { _eq: 'Sold' } },
        notVoidedPayout,
        {
          _or: [
            { tp_payment_status: { _is_null: true } },
            { tp_payment_status: { _eq: '' } },
            { tp_payment_status: { _nin: ['Success', 'Inprogress', 'Failed'] } },
          ],
        },
      ],
    };
  }
  return { tp_status: { _in: SOLD_STATUSES } };
};

const SOLD_STATUS_FILTERS: Record<string, UserTicketSoldSubFilter> = {
  Sold: 'sold',
  Settled: 'settled',
  SettlementInProgress: 'settlementinprogress',
  Failed: 'failed',
  Voided_Payout: 'voidedpayout',
  PaidManuallyViaQA: 'paidmanuallyviaqa',
  QADeniedPayout: 'qadeniedpayout',
  UnderReview: 'underreview',
};

const statusFilterClause = (statusFilter?: string) => {
  const value = String(statusFilter || '').trim();
  if (!value || value === 'All' || value.startsWith('All ')) return null;
  if (SOLD_STATUS_FILTERS[value]) return soldSubFilterClause(SOLD_STATUS_FILTERS[value]);
  const statuses: Record<string, string[]> = {
    ToBeVerified: ['ToBeVerified'],
    Verified: ['Verified'],
    Invalid: ['Invalid'],
    List: ['List', 'Listed'],
    DelistInProgress: ['DelistInProgress', 'DelistRequested'],
    Delist: ['Delist'],
    Return: ['Return'],
    Deleted: ['Deleted'],
    ListingRequestCancelled: ['ListingRequestCancelled'],
    Closed: ['Closed', 'ListingRequestExpired'],
    Unsold: ['Unsold'],
  };
  const match = statuses[value];
  return match ? { tp_status: { _in: match } } : null;
};

const lastPricedBucketClause = (buckets: string[], now: Date) => {
  const normalized = new Set(
    buckets.map((value) => {
      const raw = String(value).trim();
      if (raw === '7 Days' || raw === '7-13' || raw === '7-13 Days') return '7-13';
      if (raw === '14 Days' || raw === '14-20' || raw === '14-20 Days') return '14-20';
      if (raw === '21 Days' || raw === '21-29' || raw === '21-29 Days') return '21-29';
      if (raw === '30+ Days' || raw === '30plus' || raw === '30+ day') return '30plus';
      return raw;
    }),
  );
  const day = 24 * 60 * 60 * 1000;
  const rangeFor = (minAgeDays: number, maxAgeDays: number | null) => {
    const newest = new Date(now.getTime() - minAgeDays * day).toISOString();
    if (maxAgeDays == null) return { last_priced_at: { _lte: newest } };
    const oldest = new Date(now.getTime() - maxAgeDays * day).toISOString();
    return { last_priced_at: { _gt: oldest, _lte: newest } };
  };
  const clauses = [];
  if (normalized.has('7-13')) clauses.push(rangeFor(7, 14));
  if (normalized.has('14-20')) clauses.push(rangeFor(14, 21));
  if (normalized.has('21-29')) clauses.push(rangeFor(21, 30));
  if (normalized.has('30plus')) clauses.push(rangeFor(30, null));
  if (!clauses.length) return null;
  return clauses.length === 1 ? clauses[0] : { _or: clauses };
};

const searchClause = (search: string) => {
  const token = search.trim();
  if (!token) return null;
  const pattern = `%${token.replace(/[%_]/g, '')}%`;
  const clauses: Record<string, unknown>[] = [
    { e_name: { _ilike: pattern } },
    { e_address: { _ilike: pattern } },
    { l_name: { _ilike: pattern } },
    { tp_section: { _ilike: pattern } },
    { tp_row: { _ilike: pattern } },
    { tp_status: { _ilike: pattern } },
  ];
  if (/^\d+$/.test(token)) {
    clauses.push({ tp_seat_no: { _eq: Number(token) } });
  }
  const amount = Number(token.replace(/[$,]/g, ''));
  if (token.replace(/[$,]/g, '') !== '' && Number.isFinite(amount)) {
    clauses.push({ tp_list_price: { _eq: amount } });
    clauses.push({ tp_sold_amount: { _eq: amount } });
  }
  return { _or: clauses };
};

export const buildUserTicketOrderBy = (
  sort: ManageTicketTableSort | null,
  tab: string,
) => {
  if (!sort) return tab === 'Delisted' ? DELISTED_TAB_ORDER_BY : MANAGE_TICKETS_ORDER_BY;
  const priceField = tab === 'Sold' ? 'tp_sold_amount' : 'tp_list_price';
  const fields: Record<string, string> = {
    event: 'e_date',
    section: 'tp_section',
    row: 'tp_row',
    price: priceField,
    lastPriced: 'last_priced_at',
  };
  const field = fields[sort.column];
  if (!field) return MANAGE_TICKETS_ORDER_BY;
  return [{ [field]: sort.direction }, { tp_id: 'asc' }];
};

export const buildUserTicketPageWhere = (
  input: UserTicketListQueryInput,
  barcodeFilter = input.barcodeFilter,
) => {
  if (!input.stripeMatches) return emptyUserTicketWhere(input.userId);

  const and: Record<string, unknown>[] = [{ u_id: { _eq: input.userId } }];
  const now = input.now ?? new Date();

  if (input.tab === 'Sold') {
    and.push(soldSubFilterClause(input.soldSubFilter));
  } else {
    const statuses = TAB_STATUSES[input.tab];
    if (statuses) and.push({ tp_status: { _in: statuses } });
    const status = statusFilterClause(input.statusFilter);
    if (status) and.push(status);
  }

  if (input.tab !== 'All') {
    and.push({
      _not: {
        _and: [
          { tp_status: { _eq: 'ToBeVerified' } },
          { tp_updated_at: { _lte: getFailedTransferCutoffIso() } },
        ],
      },
    });
  }

  const search = searchClause(input.search);
  if (search) and.push(search);

  const dates = dateBounds(input.dateFilter, input.customFromDate, input.customToDate, now);
  if (dates) {
    and.push({ e_date: { _gte: dates.fromYmd, _lte: dates.toYmd } });
  }

  if (input.tab === 'Listed' || input.tab === 'Sold') {
    if (barcodeFilter === 'attached') and.push(attachedBarcode);
    if (barcodeFilter === 'missing') and.push(missingBarcode);
    if (barcodeFilter === 'none') and.push(noBarcode);
  }

  if (input.tab === 'Listed') {
    if (input.vlShareFilter === 'shared') and.push({ tp_vl_share: { _eq: true } });
    if (input.vlShareFilter === 'unshared') and.push({ tp_vl_share: { _eq: false } });
    const pid = getPidStatusFilterForQuery(input.pidStatusFilter);
    if (Object.keys(pid).length) and.push(pid);
    if (input.onHoldOnly) and.push({ on_hold: { _eq: true } });
    if (input.pricingMethodFilter === 'NONE') {
      and.push({
        _or: [
          { tp_pricing_method: { _is_null: true } },
          { tp_pricing_method: { _eq: '' } },
        ],
      });
    } else if (input.pricingMethodFilter !== 'all') {
      and.push({ tp_pricing_method: { _eq: input.pricingMethodFilter } });
    }
    if (input.lastPricedRange) {
      and.push({
        last_priced_at: { _gte: input.lastPricedRange.from, _lte: input.lastPricedRange.to },
      });
    }
    if (input.lastPricedCustomRange) {
      and.push({
        last_priced_at: {
          _gte: input.lastPricedCustomRange.from,
          _lte: input.lastPricedCustomRange.to,
        },
      });
    }
    const buckets = lastPricedBucketClause(input.lastPricedBuckets, now);
    if (buckets) and.push(buckets);
  }

  return { _and: and };
};

export const buildUserTicketListQueryFilters = (input: UserTicketListQueryInput) => ({
  where: buildUserTicketPageWhere(input),
  barcodeAttachedWhere: buildUserTicketPageWhere({ ...input, barcodeFilter: 'attached' }),
  barcodeMissingWhere: buildUserTicketPageWhere({ ...input, barcodeFilter: 'missing' }),
  orderBy: buildUserTicketOrderBy(input.sort, input.tab),
  verificationCutoff: getFailedTransferCutoffIso(),
});
